import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Loader2,
} from "lucide-react";
import { DatePicker } from "@/components/ui/DatePicker";
import { useAuthStore } from "@/store/auth";
import { formatPhone } from "@/utils/formatPhone";
import { SMS_LOG_PAGE_SIZE, smsLogApi } from "../api/smsLog";
import type { SmsLogParams } from "../api/smsLog";
import type { SmsLogStatus, SmsOccasion } from "../types";

const inputCls =
  "w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]";

const STATUS_OPTIONS: { value: SmsLogStatus | ""; label: string }[] = [
  { value: "", label: "Wszystkie statusy" },
  { value: "sent", label: "Wysłane" },
  { value: "failed", label: "Błąd" },
  { value: "skipped", label: "Pominięte" },
];

const STATUS_CLS: Record<SmsLogStatus, string> = {
  sent: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  skipped: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function SmsLogPage() {
  const user = useAuthStore((s) => s.user);
  // Ten sam krąg co reszta Wsparcia: deweloper, CEO albo dyrektor techniczny.
  // Backend i tak pilnuje dostępu (CanViewSmsLog) — to tylko UI.
  const canView = !!(
    user?.is_developer ||
    user?.is_ceo ||
    (user?.role === "director" && user?.is_technical_director)
  );

  const [search, setSearch] = useState("");
  const [occasion, setOccasion] = useState("");
  const [statusFilter, setStatusFilter] = useState<SmsLogStatus | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const { data: occasions = [] } = useQuery<SmsOccasion[]>({
    queryKey: ["sms-log-occasions"],
    queryFn: () => smsLogApi.occasions(),
    staleTime: 5 * 60_000,
    enabled: canView,
  });

  const params: SmsLogParams = {
    ...(search.trim() && { search: search.trim() }),
    ...(occasion && { occasion }),
    ...(statusFilter && { status: statusFilter }),
    ...(dateFrom && { date_from: dateFrom }),
    ...(dateTo && { date_to: dateTo }),
  };

  const paramsKey = JSON.stringify(params);
  useEffect(() => {
    setPage(1);
  }, [paramsKey]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["sms-log", params, page],
    queryFn: () => smsLogApi.list({ ...params, page }),
    placeholderData: keepPreviousData,
    enabled: canView,
  });

  if (!canView) {
    return <div className="p-6 text-sm text-red-500">Brak dostępu.</div>;
  }

  const entries = data?.data ?? [];
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / SMS_LOG_PAGE_SIZE));
  const btnCls =
    "flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-gray-700 dark:text-gray-300";

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 space-y-6">
      <div className="flex items-center gap-3">
        <MessageSquare size={22} className="text-[var(--color-primary)]" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">SMS</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-0.5" />
        </div>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Rejestr wszystkich SMS-ów wychodzących z ELGW5 — podgląd, bez możliwości
        edycji. Treść kodów logowania (2FA) jest maskowana.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input
          type="text"
          placeholder="Szukaj: numer, odbiorca, treść..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={inputCls}
        />
        <select
          value={occasion}
          onChange={(e) => setOccasion(e.target.value)}
          className={inputCls}
        >
          <option value="">Wszystkie okazje</option>
          {occasions.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as SmsLogStatus | "")}
          className={inputCls}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 whitespace-nowrap">Data od:</label>
          <DatePicker value={dateFrom} onChange={setDateFrom} />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 whitespace-nowrap">Data do:</label>
          <DatePicker value={dateTo} onChange={setDateTo} />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 size={24} className="animate-spin text-[var(--color-primary)]" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          Brak SMS-ów spełniających kryteria
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-400">
            Pokazano {entries.length} z {count}
          </p>
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Data</th>
                  <th className="px-3 py-2 text-left font-medium">Odbiorca</th>
                  <th className="px-3 py-2 text-left font-medium">Okazja</th>
                  <th className="px-3 py-2 text-left font-medium">Treść</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {entries.map((e) => (
                  <tr
                    key={e.id}
                    className="align-top hover:bg-gray-50 dark:hover:bg-gray-800/40"
                  >
                    <td className="px-3 py-2 whitespace-nowrap tabular-nums text-gray-600 dark:text-gray-300">
                      {formatDateTime(e.created_at)}
                    </td>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                      {e.recipient_name && (
                        <div className="font-medium">{e.recipient_name}</div>
                      )}
                      <div className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                        {formatPhone(e.phone)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                      <div>{e.occasion_label}</div>
                      {e.occasion_audience && (
                        <div className="text-xs text-gray-400">{e.occasion_audience}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300 max-w-md">
                      <div className="whitespace-pre-wrap break-words">{e.message}</div>
                      {e.error && (
                        <div className="text-xs text-red-500 mt-1 break-words">{e.error}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_CLS[e.status]}`}
                      >
                        {e.status_label}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 pt-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={isFetching || page <= 1}
                className={btnCls}
              >
                <ChevronLeft size={14} />
                Poprzednia
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                Strona {page} z {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={isFetching || page >= totalPages}
                className={btnCls}
              >
                Następna
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
